import { getPatientContext } from '@/pages/api/scribeAPIV2';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { toast, ToastActionButton } from '@heidi/design-system/toast';
import { PatientContextItemLabelMap } from '@heidi/patients/contracts';
import { isPatientContextAutoExtractEnabled as IS_PATIENT_CONTEXT_AUTO_EXTRACT_ENABLED } from '@heidi/patients/flags';
import { logger } from '@heidi/runtime/logger';
import { GET_SESSION_BY_ID, type QuerySessionResponse } from '@heidi/session/contracts';
import { GET_USER, type UserResponse } from '@heidi/workspace/contracts';
import i18next from 'i18next';
import produce from 'immer';
import { getDefaultStore } from 'jotai';
import React from 'react';

export const extractPatientProfileContextFromTranscript = async (
  sessionId: string
): Promise<void> => {
  // Check if session has a patient_profile before proceeding
  const sessionData = reactQueryClient.getQueryData<QuerySessionResponse>([
    GET_SESSION_BY_ID,
    { sessionId },
  ]);

  const hasPatientProfile = sessionData?.patient_profile;

  // Opt-out gate: the user can turn auto-extract off in Data Management or via
  // the in-popup choice. Default (undefined/true) runs; only an explicit false
  // skips. Read from the user query cache since this isn't a React hook.
  const user = reactQueryClient.getQueryData<UserResponse>([GET_USER]);
  const hasOptedOut =
    user?.session_settings?.intends_to_auto_extract_patient_context === false;

  // If auto-extract is not enabled, the user opted out, or the session has no
  // patient profile, skip.
  if (
    !sessionData ||
    !hasPatientProfile ||
    !IS_PATIENT_CONTEXT_AUTO_EXTRACT_ENABLED() ||
    hasOptedOut
  ) {
    return;
  }

  // The UI door evaluates message and table modules at load. This runs from
  // the recording model, whose suites mock those modules narrowly, so the
  // atoms load only once extraction actually runs.
  const { isGeneratingPatientContextAtom, patientDetailsPopupOpenAtom } =
    await import('@heidi/patients');

  const jotaiStore = getDefaultStore();

  jotaiStore.set(isGeneratingPatientContextAtom, true);

  try {
    const { data } = await getPatientContext(sessionId);
    const patientContext = Object.fromEntries(
      Object.entries(data).filter(
        ([key, value]) => !!value && key in PatientContextItemLabelMap
      )
    );
    reactQueryClient.setQueryData([GET_SESSION_BY_ID, { sessionId }], (oldData) => {
      if (!oldData) return undefined;
      return produce(oldData, (draft: QuerySessionResponse) => {
        draft.extracted_patient_context = patientContext;
      });
    });

    // Show toast notification if patient context was extracted
    if (Object.keys(patientContext).length > 0) {
      const handleReviewClick = () => {
        jotaiStore.set(patientDetailsPopupOpenAtom, true);
      };

      toast({
        title: i18next.t('patient.unsavedPatientDetailsFound'),
        action: React.createElement(
          ToastActionButton,
          { onClick: handleReviewClick },
          `${i18next.t('patient.review')}`
        ),
      });
    }
  } catch (error) {
    logger.error('Failed to get patient profile context from session:', sessionId, error);
  } finally {
    jotaiStore.set(isGeneratingPatientContextAtom, false);
  }
};
